import { Button, Divider, Form, Input, InputNumber } from 'antd';
import { Formik } from 'formik';
import { useTranslate } from '@tolgee/react';
import { match, when } from 'ts-pattern';
import { cx } from '@emotion/css';
import { Panel } from './Panel';
import { TypeSource } from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import {
  useReplyMsgs,
  ReplyMsgsFormValues,
} from 'views/Conversation/Sider/ToDoBundle/useReplyMsgs';
import { useTemplateNode } from 'views/Conversation/Sider/ToDoBundle/useTemplateNode';
import { CopyMedia, HotkeyTips } from 'views/Conversation/Sider/ToDoBundle/TemplateNode';
import FormField from 'components/FormField';
import StatusButton from 'components/StatusButton';
import { useState } from 'react';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { isMac } from 'utils/is';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';

export interface ReplyMsgProps extends ReplyMsgsFormValues {
  typeSource: TypeSource;
  conversationId: string;
}

interface TemplateItemProps {
  index: number;
  active: boolean;
  template: Todo.PolicyTemplate;
  showLabel: boolean;
  conversationId: string;
  doubleMode: boolean;
  todoId?: string;
  onMouseEnter?: (index: number) => void;
}

function TemplateItem(props: TemplateItemProps) {
  const { index, active, template, showLabel, conversationId, doubleMode, todoId, onMouseEnter } =
    props;

  const templateNode = useTemplateNode(
    template,
    {
      active,
      showLabel,
      submitEffect: false,
      doubleMode,
      todoId,
    },
    {
      conversationId,
      readonly: false,
      currency: '',
      tradeAmount: undefined,
    },
  );

  const {
    initialValues,
    initialTouched,
    mtToShow,
    mtToShowNext,
    unit,
    concepts,
    needValidateRef,
    inputRef,
    getConceptText,
    resultTypeLabel,
    hotkey,
    disabled,
    sendEditRef,
    send,
    edit,
    tradeAmount,
    hasEditContent,
  } = templateNode;

  const { t } = useTranslate();

  const handleMouseEnter = () => {
    onMouseEnter?.(index);
  };

  return (
    <div
      className={cx(
        'rounded',
        active
          ? 'border border-solid border-[var(--fett-scenario-accent-text)] bg-[var(--fett-scenario-accent-bg)]'
          : 'border border-solid border-[var(--fett-bg-subtle)] bg-[var(--fett-bg-subtle)] hover:border-[var(--fett-scenario-accent-text)] hover:bg-[var(--fett-scenario-accent-bg)]',
      )}
      onMouseEnter={handleMouseEnter}
    >
      <Formik
        enableReinitialize
        initialTouched={initialTouched}
        initialValues={initialValues}
        onSubmit={() => {}}
        validateOnBlur
        validateOnChange
      >
        {({ validateForm, values }) => {
          const addonUnitValues = {
            ...values,
            退款金额: values['退款金额'] ? `${unit} ${values['退款金额']}` : '',
            补偿金额: values['补偿金额'] ? `${unit} ${values['补偿金额']}` : '',
          };
          sendEditRef.current = {
            send: () =>
              send(validateForm as () => Promise<Record<string, unknown>>, addonUnitValues),
            edit: () =>
              edit(validateForm as () => Promise<Record<string, unknown>>, addonUnitValues),
          };
          return (
            <Form layout="vertical" className="[&_.ant-form-item]:mb-0">
              <div className="mb-[12px] last:mb-0">
                {showLabel && (
                  <>
                    <div className="flex border-0 border-b border-solid border-[var(--fett-border-default)] px-4 items-center py-[7px] leading-[18px] text-[12px] text-[var(--fett-text-primary)] justify-between">
                      <div className="font-medium">{resultTypeLabel}</div>
                      <span className="text-[var(--fett-text-tertiary)]">
                        {hotkey}+{index + 1}
                      </span>
                    </div>
                    <Divider className="!my-0" />
                  </>
                )}
                <div className="p-3 text-[var(--fett-text-secondary)] whitespace-pre-wrap leading-5">
                  <div className={cx(doubleMode ? 'line-clamp-3' : '')}>
                    {mtToShow.renderTemplate(addonUnitValues)}
                  </div>
                  <div className="mt-[4px] flex flex-wrap gap-[8px]">
                    {mtToShow.images.map((url) => (
                      <CopyMedia key={url} type="image" url={url} />
                    ))}
                    {mtToShow.videos.map((url) => (
                      <CopyMedia key={url} type="video" url={url} />
                    ))}
                  </div>
                  {mtToShowNext && mtToShowNext !== mtToShow && (
                    <div className="border-0 border-t border-dashed border-[var(--fett-border-default)] mt-[6px] pt-[6px]">
                      {mtToShowNext.renderTemplate(addonUnitValues)}
                    </div>
                  )}
                </div>
                {concepts.map((c) => (
                  <div
                    className="px-4 py-[7px] border-0 border-t border-solid border-[var(--fett-border-default)]"
                    key={c.id}
                  >
                    {match(c)
                      .with(
                        {
                          id: when((id) =>
                            [
                              '物流单号',
                              '使用方法',
                              '优惠券面值',
                              '优惠券时限',
                              '补偿方式',
                              '发货时间',
                              '取消原因',
                              '拒绝原因',
                            ].includes(id),
                          ),
                        },
                        (i) => (
                          <div className="flex items-start" key={i.id}>
                            <div className="flex-shrink-0 mr-[4px] leading-[34px]">
                              {getConceptText(i.label)}:
                            </div>
                            <FormField
                              name={i.id}
                              validate={(value) => {
                                if (!value && needValidateRef.current) {
                                  return t('department.请输入x', {
                                    x: getConceptText(i.label),
                                  });
                                }
                              }}
                            >
                              {({ field }) => (
                                <Input
                                  ref={inputRef}
                                  className="flex-grow"
                                  placeholder={t('department.请输入x', {
                                    x: getConceptText(i.label),
                                  })}
                                  {...field}
                                />
                              )}
                            </FormField>
                          </div>
                        ),
                      )
                      .with(
                        {
                          id: when((id) => ['退款金额', '补偿金额'].includes(id)),
                        },
                        (i) => (
                          <div className="flex items-start" key={i.id}>
                            <div className="flex-shrink-0 mr-[4px] leading-[34px]">
                              {getConceptText(i.label)}:
                            </div>
                            <FormField
                              name={i.id}
                              validate={(value) => {
                                if (!value && needValidateRef.current) {
                                  return t('department.请输入x', {
                                    x: getConceptText(i.label),
                                  });
                                }
                              }}
                            >
                              {({ field }) => (
                                <div className="!w-full flex items-center gap-2">
                                  <InputNumber
                                    ref={inputRef}
                                    className="flex-grow"
                                    placeholder={t('department.请输入x', {
                                      x: getConceptText(i.label),
                                    })}
                                    addonBefore={unit}
                                    max={Number(tradeAmount) || undefined}
                                    {...field}
                                  />
                                  {tradeAmount && (
                                    <div className="flex-shrink-0 max-w-[160px] truncate text-[var(--fett-text-tertiary)] text-xs">
                                      {t('translation.订单金额')}: {unit} {tradeAmount}
                                    </div>
                                  )}
                                </div>
                              )}
                            </FormField>
                          </div>
                        ),
                      )
                      .otherwise(() => null)}
                  </div>
                ))}
                {!disabled && (
                  <div className="flex justify-end gap-4 px-3">
                    <StatusButton
                      type="link"
                      className="!px-0"
                      onClick={() => sendEditRef.current?.send() ?? Promise.resolve()}
                    >
                      {t('发送')}
                    </StatusButton>
                    <Button
                      type="link"
                      className="!px-0"
                      onClick={() => sendEditRef.current?.edit()}
                      disabled={!hasEditContent}
                    >
                      {t('编辑')}
                    </Button>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

interface ReplyMsgContentProps extends ReplyMsgProps {
  filterTemplates: Todo.PolicyTemplate[];
  showOriginalText: boolean;
  filterSelfTemplate: Todo.PolicyTemplate | null;
}

const ReplyMsgContent = (props: ReplyMsgContentProps) => {
  const { id, conversationId, filterTemplates, showOriginalText, filterSelfTemplate } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslate();

  const hasList = filterTemplates.length > 0;

  useKeyboardHandler(
    isMac ? ['meta + ArrowUp'] : ['alt + ArrowUp'],
    (e) => {
      console.log('ArrowUp', e);
      e.preventDefault();
      setActiveIndex(activeIndex - 1);
    },
    {
      context: () => hasList && activeIndex > 0,
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.TOOLBAR,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  useKeyboardHandler(
    isMac ? ['meta + ArrowDown'] : ['alt + ArrowDown'],
    (e) => {
      console.log('ArrowDown', e);
      e.preventDefault();
      setActiveIndex(activeIndex + 1);
    },
    {
      context: () => hasList && activeIndex < filterTemplates.length - 1,
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.TOOLBAR,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  return (
    <Panel title={t('推荐回复')}>
      <HotkeyTips />
      {hasList ? (
        <div className="space-y-3">
          {filterTemplates.map((template, index) => (
            <TemplateItem
              key={template.resultType}
              index={index}
              active={index === activeIndex}
              template={template}
              showLabel
              conversationId={conversationId}
              doubleMode={showOriginalText}
              todoId={id}
              onMouseEnter={setActiveIndex}
            />
          ))}
        </div>
      ) : filterSelfTemplate ? (
        <div className="mt-2">
          <TemplateItem
            index={0}
            active
            template={filterSelfTemplate}
            showLabel={false}
            conversationId={conversationId}
            doubleMode={showOriginalText}
            todoId={id}
          />
        </div>
      ) : null}
    </Panel>
  );
};

export const ReplyMsg = (props: ReplyMsgProps) => {
  const { typeSource, id, region, type, operateResult } = props;
  const replyMsgs = useReplyMsgs(typeSource, { id, region, type, operateResult });

  if (!replyMsgs.shouldRender) {
    return null;
  }

  const { showOriginalText, filterTemplates, filterSelfTemplate } = replyMsgs;

  return (
    <ReplyMsgContent
      {...props}
      filterTemplates={filterTemplates}
      showOriginalText={showOriginalText}
      filterSelfTemplate={filterSelfTemplate}
    />
  );
};
